
import React from 'react';
import { Building2, Users, MapPin, Layers3, Home, UserCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Customer } from '@/lib/api/types';
import StatItem from '@/components/stats/StatItem';
import { cn } from '@/lib/utils';

interface CustomerCardProps {
  customer: Customer;
  onSelect: (customer: Customer) => void;
  isLoading?: boolean;
  showStats?: boolean;
  isLastActive?: boolean;
}

const CustomerCard: React.FC<CustomerCardProps> = ({ 
  customer, 
  onSelect,
  isLoading = false,
  showStats = true,
  isLastActive = false
}) => {
  // Simplified logic: if stats exist and showStats is true, then display stats
  const shouldShowStats = showStats && customer.stats !== undefined;
  
  return (
    <Card className={cn(
      "w-full h-full transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-2",
      isLastActive 
        ? "border-primary bg-primary/5 shadow-lg shadow-primary/20" 
        : "border-border hover:border-primary/50 hover:bg-accent/30"
    )}>
      <CardHeader className="pb-4 pt-6">
        <div className="flex flex-col items-center gap-4">
          <Avatar className="h-24 w-24 rounded-xl shadow-md">
            {customer.logo ? (
              <AvatarImage src={customer.logo} alt={`${customer.name} logo`} className="object-cover" />
            ) : (
              <AvatarFallback className="rounded-xl bg-primary/10 text-primary">
                <Building2 className="h-12 w-12" />
              </AvatarFallback>
            )}
          </Avatar>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold text-foreground">{customer.name}</h3>
            {isLastActive && (
              <div className="flex items-center justify-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Last Active</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      {shouldShowStats && (
        <CardContent className="px-6 pb-4">
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <StatItem 
              icon={Building2}
              value={customer.stats?.site_count || 0}
              label="Sites"
              className="hover:scale-110 transition-transform"
            />
            <StatItem 
              icon={MapPin}
              value={customer.stats?.address_count || 0}
              label="Addresses"
              className="hover:scale-110 transition-transform"
            />
            <StatItem 
              icon={Layers3}
              value={customer.stats?.floor_count || 0}
              label="Floors"
              className="hover:scale-110 transition-transform"
            />
            <StatItem 
              icon={Home}
              value={customer.stats?.room_count || 0}
              label="Rooms"
              className="hover:scale-110 transition-transform"
            />
            <StatItem 
              icon={Users}
              value={customer.stats?.tenant_count || 0}
              label="Tenants"
              className="hover:scale-110 transition-transform"
            />
            <StatItem 
              icon={UserCircle}
              value={customer.stats?.user_count || 0}
              label="Users"
              className="hover:scale-110 transition-transform"
            />
          </div>
        </CardContent>
      )}
      
      <CardFooter className="px-6 pb-6">
        <Button
          variant={isLastActive ? "default" : "outline"}
          className="w-full h-11 text-sm font-medium"
          onClick={() => onSelect(customer)}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : isLastActive ? "Resume Session" : "Select Customer"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CustomerCard;
